import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';
import AnalyzerHeader from './AnalyzerHeader';
import ScoreCard from './ScoreCard';
import { algorithmCatalog, ALGO_BACKUP } from './algorithmCatalog';
import { isFiniteNumber } from './scoreUtils';
import NeighborDataChipConnected from './NeighborDataChipConnected';
import { NeighborDataIncompleteCard } from './NeighborDataStatus';
import { useAnalyserGate } from './useAnalyserGate';
import { useNeighborData } from './NeighborDataContext';

const formatRSSI = (value) =>
  isFiniteNumber(value) ? `${value.toFixed(0)} dBm` : '—';

const BackupParentsPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const catalog = algorithmCatalog[ALGO_BACKUP];
  const { refresh: refreshDataStatus } = useNeighborData();
  const { missingSerials, clearMissing, runGuarded, scanMissing, scanningSerials } = useAnalyserGate();

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    const outcome = await runGuarded(() => api.getBackupParentsAnalysis());
    setLoading(false);
    if (outcome.ok) {
      clearMissing();
      setResult(outcome.data);
      enqueueSnackbar('Backup-parents analysis complete.', { variant: 'success' });
      return;
    }
    if (outcome.gated) {
      setResult(null);
      enqueueSnackbar('Backup parents cannot run: neighbour data is incomplete.', { variant: 'error' });
      return;
    }
    enqueueSnackbar(outcome.error?.message || 'Failed to run backup-parents analysis', { variant: 'error' });
  }, [enqueueSnackbar, runGuarded, clearMissing]);

  const handleScanMissing = useCallback(async () => {
    const summary = await scanMissing();
    if (!summary) return;
    enqueueSnackbar(
      summary.failed === 0
        ? `Triggered neighbour scan for ${summary.accepted} device(s).`
        : `Triggered ${summary.accepted} scan(s); ${summary.failed} failed to start.`,
      { variant: summary.failed === 0 ? 'info' : 'warning' },
    );
    refreshDataStatus();
  }, [scanMissing, enqueueSnackbar, refreshDataStatus]);

  const summary = result?.summary;
  const entries = result?.devices || [];

  return (
    <Box>
      <AnalyzerHeader
        catalog={catalog}
        loading={loading}
        onRun={runAnalysis}
        hasResult={!!result}
        rightContent={<NeighborDataChipConnected />}
      />

      {missingSerials.length > 0 && (
        <NeighborDataIncompleteCard
          missingSerials={missingSerials}
          onScanMissing={handleScanMissing}
          scanningSerials={scanningSerials}
        />
      )}

      {result && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <ScoreCard score={result.score} />
            </Box>
            <Paper variant="outlined" sx={{ flex: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Resilience
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                <Chip size="small" label={`${summary?.total_devices ?? 0} devices`} />
                <Chip size="small" color="primary" label={`${summary?.nodes_analyzed ?? 0} nodes`} />
                <Chip size="small" color="success" label={`${summary?.nodes_with_backup ?? 0} with backup`} />
                <Chip size="small" color="error" label={`${summary?.nodes_without_backup ?? 0} stranded`} />
              </Stack>
            </Paper>
          </Stack>

          {entries.length === 0 ? (
            <Alert severity="info">No Nodes available to analyse.</Alert>
          ) : (
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Node</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Current parent</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Backups</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Best alternative</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => {
                      const best = entry.backups?.[0];
                      return (
                        <TableRow key={entry.node_serial} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {entry.node_serial}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {entry.current_parent || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={(entry.backups?.length || 0) > 0 ? 'success' : 'error'}
                              label={entry.backups?.length || 0}
                            />
                          </TableCell>
                          <TableCell>
                            {best ? (
                              <Tooltip title={`Path cost ${best.path_cost?.toFixed(2)}, improvement ${best.improvement?.toFixed(2)}`}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                    {best.parent_serial}
                                  </Typography>
                                  <Chip size="small" variant="outlined" label={best.band} />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatRSSI(best.rssi_down_dbm)}
                                  </Typography>
                                </Stack>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {entry.available ? (
                              <Tooltip title="Has at least one viable backup parent">
                                <CheckCircleIcon fontSize="small" color="success" />
                              </Tooltip>
                            ) : (
                              <Tooltip title={entry.reason || 'No backup parent available'}>
                                <ReportProblemIcon fontSize="small" color="error" />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {!result && !loading && (
        <Alert severity="info">
          Click <strong>Run</strong> to compute backup-parent posture for every Node. Read-only — no actions to apply.
        </Alert>
      )}
    </Box>
  );
};

export default BackupParentsPanel;
